// The credit for an image, looked up from the image URL itself — Commons,
// Google or Tiqets, one shape for all (docs/WIKIMEDIA_ATTRIBUTION.md, Design).
//
// Keying on the URL on screen is what keeps a credit on its own photo: a
// Commons file's name is in its URL and a Google photo's reference is in its
// proxy URL, so the answer can't describe another picture the way a field
// carried beside the URL can (four surfaces got that pairing wrong).
//
// Lookups are batched: every request made within BATCH_DELAY_MS goes out as
// one POST /api/photo-credits, so a page full of photos costs a call or two.
// Answers are cached for the session by FILE (a Commons file at 120px and at
// 1920px is one entry), "no credit" included.

import { useEffect, useState } from 'react';
import { getApiUrl } from '../config/api';
import { isWikimediaMediaUrl, wikimediaFilePageUrl } from './imageSource';
import { parseGooglePhotoUrl } from './googlePhotoCredit';

const BATCH_DELAY_MS = 30;
const MAX_BATCH = 50; // the endpoint's cap
const RETRY_DELAY_MS = 1500;
const TIQETS_HOST = 'aws-tiqets-cdn.imgix.net';

const EMPTY = {
  author: null,
  author_url: null,
  author_photo_url: null,
  license: null,
  license_url: null,
  public_domain: false,
  title: null,
  source_url: null,
};

export const TIQETS_CREDIT = Object.freeze({ ...EMPTY, source: 'tiqets' });

/**
 * Which credited source an image URL is from, and the key its credit is
 * cached under — or null for images that owe no credit (our users' photos,
 * category fallbacks). Commons files without a Commons file page (per-wiki
 * uploads) are not creditable this way and return null too.
 * @returns {{ source: 'commons'|'google'|'tiqets', key: string } | null}
 */
export function photoCreditSource(url) {
  if (typeof url !== 'string' || !url) return null;
  if (isWikimediaMediaUrl(url)) {
    const filePage = wikimediaFilePageUrl(url);
    return filePage ? { source: 'commons', key: filePage } : null;
  }
  const google = parseGooglePhotoUrl(url);
  if (google) return { source: 'google', key: `g|${google.photoReference}|${google.placeId || ''}` };
  if (url.includes(TIQETS_HOST)) return { source: 'tiqets', key: 'tiqets' };
  return null;
}

/**
 * What can be shown before (or without) an answer: for Commons, a credit
 * whose only content is the file page, which itself states author and licence
 * — so a Commons photo is never uncredited while its lookup is in flight, or
 * if it fails. Tiqets needs no lookup. Google shows nothing until it knows.
 */
export function provisionalCredit(url) {
  const s = photoCreditSource(url);
  if (!s) return null;
  if (s.source === 'commons') return { ...EMPTY, source: 'commons', source_url: s.key, provisional: true };
  if (s.source === 'tiqets') return TIQETS_CREDIT;
  return null;
}

/**
 * The text of a credit field some responses still carry beside a URL (a bare
 * string, or the older `{ text, uri }`), for PhotoCredit's `fallbackText` —
 * used only where the URL itself names no credited source, or for Tiqets.
 */
export function legacyCreditText(attribution) {
  if (!attribution) return null;
  if (typeof attribution === 'string') return attribution;
  return typeof attribution.text === 'string' && attribution.text ? attribution.text : null;
}

// key → Promise<credit|null>
const cache = new Map();
let pending = []; // [{ key, url, resolve, retried }]
let flushTimer = null;

function enqueue(entry, delay = BATCH_DELAY_MS) {
  pending.push(entry);
  if (!flushTimer) flushTimer = setTimeout(flush, delay);
}

async function flush() {
  flushTimer = null;
  const batch = pending.splice(0, MAX_BATCH);
  if (pending.length) flushTimer = setTimeout(flush, 0);
  try {
    const res = await fetch(getApiUrl('/api/photo-credits'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ urls: batch.map(e => e.url) })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { credits = {} } = await res.json();
    batch.forEach(({ url, resolve }) => resolve(credits[url] || null));
  } catch {
    // One retry (a 429 or a dropped connection is usually momentary), then
    // give up without caching, so a remount asks again.
    batch.filter(e => !e.retried).forEach(entry => enqueue({ ...entry, retried: true }, RETRY_DELAY_MS));
    batch.filter(e => e.retried).forEach(({ key, resolve }) => {
      cache.delete(key);
      resolve(null);
    });
  }
}

/** @returns {Promise<object|null>} the server's credit for the image at `url`. */
export function fetchPhotoCredit(url) {
  const s = photoCreditSource(url);
  if (!s) return Promise.resolve(null);
  if (s.source === 'tiqets') return Promise.resolve(TIQETS_CREDIT);
  if (!cache.has(s.key)) {
    cache.set(s.key, new Promise(resolve => enqueue({ key: s.key, url, resolve, retried: false })));
  }
  return cache.get(s.key);
}

/**
 * The credit for the image at `url`: the provisional one at once, the
 * server's when it arrives. Null for an image that owes none.
 */
export function usePhotoCredit(url) {
  const [credit, setCredit] = useState(() => provisionalCredit(url));
  useEffect(() => {
    const provisional = provisionalCredit(url);
    setCredit(provisional);
    const s = photoCreditSource(url);
    if (!s || s.source === 'tiqets') return undefined;
    let active = true;
    fetchPhotoCredit(url).then(c => {
      // An unknown Commons file keeps its file-page credit.
      if (active) setCredit(c || provisional);
    });
    return () => { active = false; };
  }, [url]);
  return credit;
}

// For tests.
export function __resetPhotoCreditCache() {
  cache.clear();
  pending = [];
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
}
